import { useState, useEffect } from 'react';
import { apiFetch } from '../../lib/api';

interface Spike {
  id: string;
  prNumber: number;
  prTitle: string;
  prUrl: string | null;
  filesChanged: number;
  estimatedPts: number;
  detectedAt: string;
}

// #3605 PR-1 (ruling 5) — null when no single active sprint resolves or it plans 0 points.
interface VelocityImpact {
  plannedPts: number | null;
  spikePts: number;
  remainingPts: number | null;
  impactPct: number | null;
  healthStatus: 'GREEN' | 'AMBER';
}

interface SpikesResponse {
  spikes: Spike[];
  totalEstimatedPts: number;
  velocityImpact: VelocityImpact;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function UnplannedWorkSection({ projectId }: { projectId: string }) {
  const [data, setData] = useState<SpikesResponse | null>(null);

  useEffect(() => {
    apiFetch<SpikesResponse>(`/projects/${projectId}/sprints/current/spikes`)
      .then(setData)
      .catch(() => {});
  }, [projectId]);

  if (!data || data.spikes.length === 0) return null;

  const { spikes, totalEstimatedPts, velocityImpact } = data;
  const isAmber = velocityImpact.healthStatus === 'AMBER';

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: `1px solid ${isAmber ? 'rgba(245,158,11,0.4)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: '16px 20px',
        marginTop: 16,
      }}
      data-testid="unplanned-work-section"
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 16 }}>&#9889;</span>
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700,
          color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          Unplanned Work This Sprint
        </span>
        <span style={{
          fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)',
        }}>
          &middot; {spikes.length} spike{spikes.length !== 1 ? 's' : ''} &middot; ~{totalEstimatedPts}pts consumed
        </span>
        {isAmber && (
          <span style={{
            background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
          }}>
            AMBER
          </span>
        )}
      </div>

      {/* Velocity impact line */}
      <p data-testid="velocity-impact" style={{
        fontFamily: 'var(--font-body)', fontSize: 12,
        color: 'var(--text-secondary)', margin: '0 0 12px',
      }}>
        {/* #3605 PR-1 (AVR 6301812703) — planned figures only when the API resolved a sprint; never "nullpts". */}
        Velocity impact: {velocityImpact.spikePts}pts consumed
        {velocityImpact.plannedPts !== null && (
          <>
            {' '}&mdash; {velocityImpact.remainingPts}pts of {velocityImpact.plannedPts}pts planned remaining
            {velocityImpact.impactPct !== null && ` (${velocityImpact.impactPct}%)`}
          </>
        )}
      </p>

      {/* Spike rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {spikes.map(spike => (
          <div
            key={spike.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px',
              background: 'rgba(0,0,0,0.02)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <span style={{ fontSize: 13 }}>&#9889;</span>
            <span style={{
              fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
              fontWeight: 700, color: 'var(--text-secondary)', flexShrink: 0,
            }}>
              PR #{spike.prNumber}
            </span>
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
            }}>
              {spike.prTitle.length > 60 ? spike.prTitle.slice(0, 60) + '...' : spike.prTitle}
            </span>
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
              color: 'var(--primary)', flexShrink: 0,
            }}>
              ~{spike.estimatedPts}pts
            </span>
            {spike.prUrl && (
              <a
                href={spike.prUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
                  color: 'var(--primary)', textDecoration: 'none', flexShrink: 0,
                }}
              >
                View PR &rarr;
              </a>
            )}
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: 11,
              color: 'var(--text-secondary)', flexShrink: 0,
            }}>
              {relativeTime(spike.detectedAt)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
